import { MouseEvent, RefObject, useEffect, useRef, useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import Text from '@atoms/Text/Text';
import Button from '@atoms/Button/Button';
import ProductListItem from '@molecules/ProductListItem/ProductListItem';
import EmptyCartModal from '@organisms/EmptyCartModal/EmptyCartModal';
import { makeProductEntries } from '@helpers/wishlistProductEntries';
import paths, { LoginQueryParams } from '@constants/paths';
import { selectMiniCartPreviewIsOpen, setIsCartPreviewOpen } from '@slices/miniCartSlice';
import Config from '@config';
import trackShoppingList from '@helpers/analyticsHelpers/trackShoppingList';
import {
  StyledHeader,
  StyledCartPreview,
  StyledEmptyCart,
  StyledButtonWrapper,
  StyledLoadingContainer,
  StyledSpinner,
  StyledPriceGrid,
  StyledLink,
  StyledButton,
} from './CartPreview.styles';
import Icon from '@atoms/Icon/Icon';
import useCart from '@hooks/useCart';
import { useTranslations } from 'next-intl';
import EmptyCart from '@molecules/EmptyCart/EmptyCart';
import AddToListComponent from '@organisms/AddToListComponent/AddToListComponent';
import AddToList from '@icons/add-to-list.svg';
import Close from '@icons/close.svg';
import Delete from '@icons/delete.svg';
import Backdrop from '@atoms/Backdrop/Backdrop';
import useResponsive from '@hooks/useResponsive';
import useFocusTrap from '@hooks/useFocusTrap';
import Heading from '@atoms/Heading/Heading';
import variables from '@root/styles/variables';
import ScrollShadow from '@components/molecules/ScrollShadow/ScrollShadow';

type Props = {
  headerRef: RefObject<HTMLDivElement | null>;
};

const CartPreview = ({ headerRef }: Props) => {
  const [isEmptyCartModalVisible, setEmptyCartModalVisibility] = useState(false);
  const [addToListModalOpen, setAddToListModalOpen] = useState(false);
  const { fromDesktop, belowDesktop } = useResponsive();
  const dispatch = useAppDispatch();

  const t = useTranslations('cartPreview');

  const { cart, isCartEmpty, hasFetchedCart } = useCart();
  const { products = [], totalUnitCount, subTotalWithDiscounts, totalPromotionDiscount } = cart || {};
  const totalWithDiscountText = subTotalWithDiscounts || t('cart.amountZero');
  const promotionDiscountText = totalPromotionDiscount || t('cart.amountZero');
  const hasProducts = products.length > 0;
  const isCartPreviewOpen = useAppSelector(selectMiniCartPreviewIsOpen);
  const productEntriesForWishlist = makeProductEntries(products);
  const hasWishlistProducts = productEntriesForWishlist?.length > 0;

  const cartPreviewRef = useRef<HTMLDivElement>(null);

  const queryParam = { from: LoginQueryParams.CART };

  const onGoToCart = (e: MouseEvent) => {
    if (isCartEmpty) e.preventDefault();
  };

  const fromDesktopRef = useRef(fromDesktop);
  useEffect(() => {
    fromDesktopRef.current = fromDesktop;
  }, [fromDesktop]);

  const cartHeightHandler = useCallback(() => {
    const stickyToolbarHeight = Config.HEIGHTS.STICKY_TOOLBAR;
    const cartElement = cartPreviewRef.current;
    if (!cartElement) return;

    const heightAttribute = CSS.supports('height: 100dvh') ? '100dvh' : '100vh';
    let newHeight = heightAttribute;

    if (fromDesktopRef.current) {
      const offsetHeight = (headerRef.current?.offsetHeight || 60) + stickyToolbarHeight;
      if (window.scrollY >= offsetHeight - stickyToolbarHeight) {
        newHeight = `calc(${heightAttribute} - ${stickyToolbarHeight}px)`;
      } else {
        newHeight = `calc(${heightAttribute} - ${offsetHeight - window.scrollY}px)`;
      }
    }
    cartElement.style.height = newHeight;
  }, [headerRef]);

  // Run on mount
  useEffect(() => {
    if (!isCartPreviewOpen) return;
    cartHeightHandler();
  }, [cartHeightHandler, isCartPreviewOpen]);

  // Run on window resize (will also run when sticky toolbar sets height)
  useEffect(() => {
    if (typeof window === 'undefined' || !isCartPreviewOpen) return;

    const target = headerRef.current ?? window.document.body;
    const resizeObserver = new ResizeObserver(() => {
      cartHeightHandler();
    });

    resizeObserver.observe(target);

    return () => {
      resizeObserver.disconnect();
    };
  }, [cartHeightHandler, isCartPreviewOpen, headerRef]);

  // Scrolling in window (desktop mode) requires handling of height because of the sticky toolbar
  useEffect(() => {
    if (!isCartPreviewOpen) return;

    let rafId: number;
    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(cartHeightHandler);
    };

    document.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [cartHeightHandler, isCartPreviewOpen]);

  // Below desktop the minicart has a backdrop, so Tab stays inside it. On desktop it's part of the page.
  useFocusTrap({ enable: isCartPreviewOpen && belowDesktop, trapRef: cartPreviewRef });

  const closeHandler = () => {
    localStorage.setItem('willys.cart-mini', false.toString());
    dispatch(setIsCartPreviewOpen(false, cart));
  };

  return (
    <>
      <StyledCartPreview
        data-testid="cart-preview"
        ref={cartPreviewRef}
        $isOpen={isCartPreviewOpen}
        role="complementary"
        aria-label={t('a11y.minicartLabel')}
        style={{ height: '100vh' }}
      >
        <StyledHeader>
          <Heading variant="h2" size="small">
            {t('cart.heading')}
          </Heading>
          <Button
            size="xsmall"
            variant="transparent"
            onClick={closeHandler}
            title={t('a11y.close')}
            aria-label={t('a11y.close')}
          >
            <Icon svg={Close} size={16} color={'black'} />
          </Button>
        </StyledHeader>

        {!hasFetchedCart ? (
          <StyledLoadingContainer>
            <Text type="subhead" size="small">
              {t('loading.title')}
            </Text>
            <StyledSpinner color="black" size={32} />
            <Text type="body" size="small">
              {t('loading.body')}
            </Text>
          </StyledLoadingContainer>
        ) : !hasProducts ? (
          <StyledEmptyCart>
            <EmptyCart fromCartPage={false} />
          </StyledEmptyCart>
        ) : (
          <ScrollShadow hideScrollBar={true}>
            {products.map((product) => (
              <ProductListItem product={product} key={`cart-row-${product.name}-${product.code}`} />
            ))}
            <StyledButtonWrapper>
              <Button
                variant="secondary"
                onClick={() => setAddToListModalOpen(true)}
                data-testid="add-to-list-button"
                aria-label={t('button.saveToList')}
                disabled={!hasWishlistProducts}
              >
                <Icon svg={AddToList} size={20} color={!hasWishlistProducts ? 'darkerGray' : 'black'} />
                {t('button.saveToList')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setEmptyCartModalVisibility(true)}
                aria-label={t('button.clearCart')}
                disabled={!hasProducts}
              >
                <Icon svg={Delete} size={20} color={!hasProducts ? 'darkerGray' : 'black'} />
                {t('button.clearCart')}
              </Button>
            </StyledButtonWrapper>
          </ScrollShadow>
        )}
        <StyledPriceGrid>
          <Text align="left" type="subhead" size="small">
            {t('cart.totalPrice', { count: totalUnitCount || 0 })}
          </Text>
          <Text align="right" type="price">
            {totalWithDiscountText}
          </Text>
          <Text align="left" type="body" size="small">
            {t('cart.youSave')}
          </Text>
          <Text align="right" type="price" size="small" color="red">
            {promotionDiscountText}
          </Text>
        </StyledPriceGrid>
        <StyledLink onClick={onGoToCart} href={paths.CART} prefetch={false}>
          <StyledButton variant="primary" disabled={!hasProducts}>
            {t('cart.goToCheckout')}
          </StyledButton>
        </StyledLink>
      </StyledCartPreview>
      {isCartPreviewOpen && belowDesktop && (
        <Backdrop zIndex={variables.zIndex.cartPreview - 1} display={true} callback={closeHandler} />
      )}
      <EmptyCartModal
        visible={isEmptyCartModalVisible}
        setVisibility={setEmptyCartModalVisibility}
        tracking={{ actionFieldList: 'minicart', category: 'minicart', action: 'removed_all_products_from_cart' }}
      />
      <AddToListComponent
        tracking={{ parent: 'minicart' }}
        addToListCallback={() => trackShoppingList({ action: 'current', parent: 'minicart' })}
        createNewListCallback={() => trackShoppingList({ action: 'new', parent: 'minicart' })}
        queryParamsIfNotLoggedIn={queryParam}
        productEntries={productEntriesForWishlist}
        onClose={() => setAddToListModalOpen(false)}
        isOpen={addToListModalOpen}
      />
    </>
  );
};

export default CartPreview;
